import { useProject } from "../context/ProjectContext";
import { useDatasources } from "../context/DatasourceContext";
import { useUserSettings } from "../context/UserSettingsContext";
import { hasDataTableWorksheets, hasModernDataTableWorksheets, updateShowDataSheetsState } from "../../excel/workbook";
import { workbookCache } from "../../excel/workbook/cache";
import { DatasourceGoneError } from "../../errors";
import { useReportData } from "../context/ReportDataContext";
import { useReportActions } from "./useReportActions";
import { refreshEstimateDatasource } from "../../excel/datasources/estimate";
import { refreshContractingDatasource } from "../../excel/datasources/bidTabs";
import { getLoadedContractingProjectId, getLoadedEstimateId, getLoadedGcgrId } from "../../excel/workbook/state";
import { fetchEstimateExport } from "../../api/estimates";
import { fetchSelectedGCGR, refreshGCGRCaches } from "../../api/gcgrs";
import { refreshScopeSheetCache } from "../../api/bids";
import { CONTRACTING_GONE_MESSAGE, ESTIMATE_GONE_MESSAGE, GCGR_GONE_MESSAGE } from "../constants";
import {
  tabulateContractingReportData,
  tabulateEstimateReportData,
  tabulateGCGRReportData,
  tabulateProjectReportData,
} from "../../excel/datasources/reportTables";
import { loadContractingData, loadEstimateData, loadGCGRData } from "../../excel/reports/dataTables";

/**
 * Builds the workbook's data tables. Shared by the drawer button that first loads them and by the
 * card that later refreshes them, so the cache/tabulate ordering lives once.
 */
export function useDataTableActions() {
  const { selectedProject, setSelectedEstimate, setSelectedGcgr } = useProject();
  const { markDataRefreshed } = useDatasources();
  const { settings, setHasDataSheets, updateSettings } = useUserSettings();
  const { refreshReportDataStatus } = useReportData();
  const { checkReportDataStaleness } = useReportActions();

  /**
   * Writes the data tables. The first load (`fresh` false) builds every loaded datasource's tables
   * from the cache and moves no `.metadata` stamp, since nothing was refetched. A refresh checks each
   * loaded datasource like a report refresh does: a stale one is refetched, retabulated and
   * rewritten; an unchanged one is rewritten from the cache. Contracting scope and the estimate's summary quantities have no change
   * signal, so a refresh always rewrites those from a fresh fetch. `onChangesDetected` runs once
   * the checks find a datasource Ediphi changed, before it's refetched. Failures THROW, so the
   * caller shows the error banner instead of a false "Data tables loaded".
   */
  const loadDataTables = async (fresh = true, { onChangesDetected }: { onChangesDetected?: () => void } = {}) => {
    if (!selectedProject) return;
    const projectId = selectedProject.id;
    // A datasource's tables are built only when the workbook already holds it. Loading data
    // tables must not add a datasource the user never chose, and an absent one costs no round-trip.
    const loadedEstimateId = await getLoadedEstimateId();
    const loadedGcgrId = await getLoadedGcgrId();
    const hasContracting = (await getLoadedContractingProjectId()) === projectId;

    if (!fresh) {
      if (loadedEstimateId) {
        const exportResult = await fetchEstimateExport(loadedEstimateId);
        if (!exportResult.success) throw exportResult.error;
        if (!exportResult.data) throw new DatasourceGoneError(ESTIMATE_GONE_MESSAGE);
        await loadEstimateData(loadedEstimateId);
      }
      if (loadedGcgrId) {
        const gcgrResult = await fetchSelectedGCGR(projectId, loadedGcgrId);
        if (!gcgrResult.success) throw gcgrResult.error;
        if (!gcgrResult.data) throw new DatasourceGoneError(GCGR_GONE_MESSAGE);
        await loadGCGRData(projectId, gcgrResult.data);
      }
      if (hasContracting) {
        const contractingData = await loadContractingData(projectId);
        if (!contractingData) throw new DatasourceGoneError(CONTRACTING_GONE_MESSAGE);
      }
      await refreshReportDataStatus();
      markDataRefreshed();
      return;
    }

    // TODO: Drop these checks once the stamps cover them. The data tables hold alternate items,
    // use-group room totals, area-matrix rows and GCGR non-staff details, which the stamps can't see.
    const staleness = await checkReportDataStaleness(undefined, ["rooms", "alternates", "area", "gcgrNonstaff"]);
    const changed =
      (loadedEstimateId && staleness?.needsEstimateRefresh) ||
      (loadedGcgrId && staleness?.needsGCGRRefresh) ||
      (hasContracting && staleness?.needsContractingRefresh);
    if (changed) {
      onChangesDetected?.();
      // Any datasource refresh writes the project row first; legacy __Report is rebuilt from it.
      await tabulateProjectReportData(projectId);
    }

    if (loadedEstimateId && staleness?.needsEstimateRefresh) {
      const exportResult = await refreshEstimateDatasource(projectId, loadedEstimateId);
      // The export answered, so an empty one means the estimate is gone rather than the endpoint being
      // unreachable — the same split the GCGR and contracting branches make below.
      if (!exportResult.success) throw exportResult.error;
      if (!exportResult.data) throw new DatasourceGoneError(ESTIMATE_GONE_MESSAGE);
      if (exportResult.data.estimate) setSelectedEstimate(exportResult.data.estimate);
      const estimateData = await loadEstimateData(loadedEstimateId);
      if (estimateData) await tabulateEstimateReportData(estimateData);
    } else if (loadedEstimateId) {
      await loadEstimateData(loadedEstimateId);
    }

    if (loadedGcgrId && staleness?.needsGCGRRefresh) {
      const gcgrResult = await refreshGCGRCaches(projectId, loadedGcgrId);
      if (!gcgrResult.success) throw gcgrResult.error;
      // Gone from the project. Rebuilding data tables answers for data tables and nothing else, so it
      // refuses rather than clearing them or the card — removing a datasource is the card's own call,
      // and the datasource paths ask before doing it.
      if (!gcgrResult.data) throw new DatasourceGoneError(GCGR_GONE_MESSAGE);
      await loadGCGRData(projectId, gcgrResult.data);
      await tabulateGCGRReportData(projectId, gcgrResult.data);
      setSelectedGcgr(gcgrResult.data);
    } else if (loadedGcgrId) {
      const gcgrResult = await fetchSelectedGCGR(projectId, loadedGcgrId);
      if (!gcgrResult.success) throw gcgrResult.error;
      if (!gcgrResult.data) throw new DatasourceGoneError(GCGR_GONE_MESSAGE);
      await loadGCGRData(projectId, gcgrResult.data);
    }

    if (hasContracting) {
      if (staleness?.needsContractingRefresh) {
        const bidGroupsResult = await refreshContractingDatasource(projectId);
        if (!bidGroupsResult.success) throw bidGroupsResult.error;
        if (!bidGroupsResult.data?.bidGroups?.length) throw new DatasourceGoneError(CONTRACTING_GONE_MESSAGE);
      } else {
        // Non-critical in getBidTabs, so a failed refetch keeps the cached copy rather than aborting
        await refreshScopeSheetCache(projectId);
      }
      const contractingData = await loadContractingData(projectId);
      // Same as the GCGR above: rebuilding data tables refuses rather than clearing when the datasource
      // has nothing left, since removing one is the card's own call.
      if (!contractingData) throw new DatasourceGoneError(CONTRACTING_GONE_MESSAGE);
      if (staleness?.needsContractingRefresh) {
        await tabulateContractingReportData(projectId, contractingData.bidGroups);
      }
    }

    // Report buttons derive enabled/disabled from the caches — recompute from the fresh data
    await refreshReportDataStatus();
    // Cards read their saved summaries, so this is what tells them a row moved.
    markDataRefreshed();
  };

  /**
   * Legacy workbooks report hasDataSheets=true (their estimateData table must keep receiving
   * refreshes) but have never been through the first-load flow that enables sheet visibility —
   * treat them as first load ONCE (flag-converged: sheet names can't converge for legacy
   * workbooks, and re-running first-load would override a user's hide-sheets preference)
   */
  const isFirstLoad = async () =>
    !settings.hasDataSheets ||
    (!(await hasModernDataTableWorksheets()) && !(await workbookCache.get("dataSheetsInitialized")));

  /** First load: turn sheet visibility on, build the tables, and mark the workbook initialized. */
  const initializeDataTables = async () => {
    // updateShowDataSheetsState opens an Excel.run; keep it off Promise.all (updateSettings here is
    // state-only) so it can't race another Office batch.
    await updateShowDataSheetsState(true);
    await updateSettings({ showDataSheets: true });
    try {
      await loadDataTables(false);
      await workbookCache.set({ dataSheetsInitialized: true });
      setHasDataSheets(true);
    } catch (error) {
      // A part-way failure still leaves sheets behind, and the Data Tables card is the only way to
      // refresh or remove them — so derive the flag from the workbook rather than leaving it false.
      // A probe that fails too falls back to the old behavior instead of masking the real error.
      setHasDataSheets(await hasDataTableWorksheets().catch(() => false));
      throw error;
    }
  };

  return { loadDataTables, isFirstLoad, initializeDataTables };
}

export default useDataTableActions;
